import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Image, Loader2, Link as LinkIcon, Grid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/lib/api';
import { toast } from 'sonner';

const ImageUploader = ({ value, onChange, label, placeholder }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');
  const [activeTab, setActiveTab] = useState('upload');
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);

  // Fetch gallery files when gallery tab is selected
  useEffect(() => {
    if (activeTab === 'gallery' && galleryFiles.length === 0) {
      fetchGalleryFiles();
    }
  }, [activeTab]);

  const fetchGalleryFiles = async () => {
    setGalleryLoading(true);
    try {
      const response = await api.get('/admin/uploads');
      setGalleryFiles(response.data.files || []);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setGalleryLoading(false);
    }
  };

  const selectFromGallery = (url) => {
    setPreview(url);
    onChange(url);
    setActiveTab('upload');
    toast.success('Görsel seçildi');
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Desteklenmeyen dosya türü. JPG, PNG, GIF, WebP veya SVG yükleyin.');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB\'den büyük olamaz.');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/admin/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const imageUrl = response.data.url;
      setPreview(imageUrl);
      onChange(imageUrl);
      toast.success('Görsel başarıyla yüklendi!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Görsel yüklenirken hata oluştu');
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp', '.svg']
    },
    maxFiles: 1,
    disabled: uploading
  });

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setPreview(url);
    onChange(url);
  };

  const clearImage = () => {
    setPreview('');
    onChange('');
  };

  return (
    <div className="space-y-3">
      {label && (
        <label className="text-sm font-medium text-slate-700">{label}</label>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 h-9">
          <TabsTrigger value="upload" className="text-xs gap-1">
            <Upload className="w-3 h-3" />
            Yükle
          </TabsTrigger>
          <TabsTrigger value="gallery" className="text-xs gap-1">
            <Grid className="w-3 h-3" />
            Galeri
          </TabsTrigger>
          <TabsTrigger value="url" className="text-xs gap-1">
            <LinkIcon className="w-3 h-3" />
            URL
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="mt-3">
          <div
            {...getRootProps()}
            className={`
              relative border-2 border-dashed rounded-lg p-6 transition-colors cursor-pointer
              ${isDragActive ? 'border-[#005CB2] bg-[#005CB2]/5' : 'border-slate-300 hover:border-[#005CB2]/50'}
              ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <input {...getInputProps()} />
            
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 text-[#005CB2] animate-spin" />
                <p className="text-sm text-slate-600">Yükleniyor...</p>
              </div>
            ) : preview ? (
              <div className="relative">
                <img 
                  src={preview.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${preview}` : preview} 
                  alt="Preview" 
                  className="max-h-32 mx-auto rounded object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    clearImage();
                  }}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                  <Image className="w-6 h-6 text-slate-400" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-slate-700">
                    {isDragActive ? 'Dosyayı bırakın' : 'Görsel sürükleyin veya tıklayın'}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    JPG, PNG, GIF, WebP, SVG (max 5MB)
                  </p>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="gallery" className="mt-3">
          {galleryLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-[#005CB2] animate-spin" />
            </div>
          ) : galleryFiles.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">
              Henüz yüklenmiş görsel yok
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1">
              {galleryFiles.map((file) => (
                <button
                  key={file.filename}
                  type="button"
                  onClick={() => selectFromGallery(file.url)}
                  className={`aspect-square rounded-lg overflow-hidden border-2 transition-all hover:border-[#005CB2] ${
                    preview === file.url ? 'border-[#005CB2] ring-2 ring-[#005CB2]/20' : 'border-transparent'
                  }`}
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${file.url}`}
                    alt={file.filename}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="url" className="mt-3">
          <div className="space-y-3">
            <Input
              value={preview || ''}
              onChange={handleUrlChange}
              placeholder={placeholder || 'https://example.com/image.jpg'}
              className="text-sm"
            />
            {preview && (
              <div className="relative p-3 bg-slate-50 rounded-lg">
                <img 
                  src={preview.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${preview}` : preview} 
                  alt="Preview" 
                  className="max-h-32 mx-auto rounded object-contain"
                  onError={(e) => {
                    e.target.src = '';
                    e.target.alt = 'Görsel yüklenemedi';
                  }}
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="absolute top-1 right-1 h-6 w-6 p-0 rounded-full"
                  onClick={clearImage}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ImageUploader;
